import { useState, useEffect } from 'react';
import HeroSection from '@/components/HeroSection';
import HeroSectionVariantB from '@/components/HeroSectionVariantB';
import AutoLaunchSection from '@/components/AutoLaunchSection';
import EmailComposerSection from '@/components/EmailComposerSection';
import IntelligentOptimizationSection from '@/components/IntelligentOptimizationSection';
import AutomatedLeadHandlingSection from '@/components/AutomatedLeadHandlingSection';
import EmailAdPlatformSection from '@/components/EmailAdPlatformSection';
import SmartToolsSection from '@/components/SmartToolsSection';
import MasterDashboardSection from '@/components/MasterDashboardSection';
import ManualCampaignSection from '@/components/ManualCampaignSection';
import StatsSection from '@/components/StatsSection';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { fadeIn } from '@/utils/animations';
import CostSavingsVisualization from '@/components/CostSavingsVisualization';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useRef } from 'react';

const WaitlistPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    budget: '',
  });
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setOpen(false);
    window.location.href = 'https://calendly.com/airlius-hts/munger-ai-demo-eb-clone';
  };

  return (
    <motion.div 
      className="min-h-screen bg-munger-darker"
      initial="initial"
      animate="animate"
      variants={fadeIn}
    >
      <HeroSectionVariantB />
      <div className="flex justify-center mt-8 mb-8">
        <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-bold" onClick={() => setOpen(true)}>
          Join Waitlist
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg mx-auto">
          <DialogHeader>
            <DialogTitle>Join the Munger AI Waitlist</DialogTitle>
            <DialogDescription>Get early access to our AI-powered cold email platform</DialogDescription>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="experience">Current Experience with Cold Email</Label>
              <Select
                onValueChange={(value) => setFormData(prev => ({ ...prev, experience: value }))}
                required
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Select your experience level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No experience</SelectItem>
                  <SelectItem value="beginner">Beginner (1-6 months)</SelectItem>
                  <SelectItem value="intermediate">Intermediate (6-12 months)</SelectItem>
                  <SelectItem value="advanced">Advanced (1+ years)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget">Monthly Marketing Budget</Label>
              <Select
                onValueChange={(value) => setFormData(prev => ({ ...prev, budget: value }))}
                required
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Select your budget range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-1000">$0 - $1,000</SelectItem>
                  <SelectItem value="1000-5000">$1,000 - $5,000</SelectItem>
                  <SelectItem value="5000-10000">$5,000 - $10,000</SelectItem>
                  <SelectItem value="10000+">$10,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <DialogClose asChild>
                <Button type="button" variant="ghost" className="mr-2">Cancel</Button>
              </DialogClose>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Submit & Schedule Demo</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <AutoLaunchSection />
      <section id="features" className="section bg-munger-dark py-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Features header if needed */}
        </div>
      </section>
      <EmailComposerSection />
      <IntelligentOptimizationSection />
      <AutomatedLeadHandlingSection />
      <EmailAdPlatformSection />
      <SmartToolsSection />
      {/* Combined dashboard and campaign section */}
      <section className="section bg-munger-dark py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <MasterDashboardSection isInGrid={true} />
            <ManualCampaignSection isInGrid={true} />
          </div>
        </div>
      </section>
      {/* Combined Stats and Cost Savings section with improved spacing */}
      <section className="section bg-munger-darker py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12"
          >
            Results & Savings
          </motion.h2>
          {/* Stats section wrapper with margin-bottom for spacing */}
          <div className="mb-24">
            <StatsSection hideTitle={true} />
          </div>
          {/* Cost savings visualization with its own heading */}
          <div className="mt-16">
            {!isMobile && (
              <motion.h3 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-2xl font-bold text-white text-center mb-12"
              >
                Significant Cost Savings
              </motion.h3>
            )}
            <CostSavingsVisualization />
          </div>
        </div>
      </section>
      {/* CTA Section at bottom before footer */}
      <section className="section bg-munger-dark py-20">
        <div className="max-w-4xl mx-auto text-center px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="section-title text-3xl md:text-4xl font-bold text-white mb-6"
          >
            Type To launch Now
          </motion.h2>
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-bold mt-8" onClick={() => setOpen(true)}>
            Join Waitlist
          </Button>
        </div>
      </section>
      <Footer />
    </motion.div>
  );
};

export default WaitlistPage; 